import { ApiError } from "@/lib/queryClient";

type ToastFn = (opts: {
  title?: string;
  description?: string;
  variant?: "default" | "destructive";
  requestId?: string;
}) => void;

/**
 * Show a destructive toast for a mutation error, automatically surfacing the
 * request ID (when the error is an ApiError) so users can reference it when
 * reporting issues to support.
 *
 * @param error  - The raw error from the onError handler.
 * @param toast  - The toast function from useToast().
 * @param title  - The toast title (e.g. "Failed to save").
 * @param description - Optional override for the description. When omitted, falls
 *                       back to `error.message` (for Error instances) or a generic
 *                       "Please try again." string.
 */
export function toastApiError(
  error: unknown,
  toast: ToastFn,
  title: string,
  description?: string,
): void {
  const message =
    description ??
    (error instanceof Error ? error.message || "Please try again." : "Please try again.");
  const requestId = error instanceof ApiError ? error.requestId : undefined;
  toast({ title, description: message, variant: "destructive", requestId });
}
